import React from 'react';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Checkbox from '@mui/joy/Checkbox';
import Tabs from '@mui/joy/Tabs';
import Tab from '@mui/joy/Tab';
import TabList from '@mui/joy/TabList';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import { Check as DoneIcon, Alarm as SnoozeIcon, Envelope as EmailIcon, Phone as PhoneIcon, ChatCircle as SmsIcon, ClipboardText as TaskIcon, Tray as AllInboxIcon } from '@phosphor-icons/react';
import { PageHelpButton } from '../../components/knowledge-base/PageHelpButton';

interface UnifiedInboxHeaderProps {
  totalCount: number;
  selectedCount: number;
  isAllSelected: boolean;
  isPartiallySelected: boolean;
  contentFilter: 'all' | 'email' | 'call' | 'sms' | 'task';
  assigneeFilter: string | null;
  sortOrder: 'oldest' | 'newest' | 'priority';
  assignees: Array<{ id: string; name: string }>;
  onSelectAll: (checked: boolean) => void;
  onBulkDone: () => void;
  onBulkSnooze: () => void;
  onContentFilterChange: (filter: 'all' | 'email' | 'call' | 'sms' | 'task') => void;
  onAssigneeFilterChange: (assignee: string | null) => void;
  onSortOrderChange: (order: 'oldest' | 'newest' | 'priority') => void;
}

const UnifiedInboxHeader: React.FC<UnifiedInboxHeaderProps> = ({
  totalCount,
  selectedCount,
  isAllSelected,
  isPartiallySelected,
  contentFilter,
  assigneeFilter,
  sortOrder,
  assignees,
  onSelectAll,
  onBulkDone,
  onBulkSnooze,
  onContentFilterChange,
  onAssigneeFilterChange,
  onSortOrderChange,
}) => {
  const handleTabChange = (event: React.SyntheticEvent | null, newValue: string | number | null) => {
    if (typeof newValue === 'string') {
      onContentFilterChange(newValue as 'all' | 'email' | 'call' | 'sms' | 'task');
    }
  };

  const sortLabels = {
    oldest: 'Oldest First',
    newest: 'Newest First',
    priority: 'Priority',
  };

  return (
    <Box
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.surface',
        p: { xs: 1.5, md: 2 },
      }}
    >
      {/* Top Row: Title and Bulk Actions */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Checkbox
            size="md"
            checked={isAllSelected}
            indeterminate={isPartiallySelected}
            onChange={(e) => onSelectAll(e.target.checked)}
            sx={{ mr: 1 }}
          />
          <Typography level="h3" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Inbox ({totalCount})
          </Typography>
          <PageHelpButton />
          {selectedCount > 0 && (
            <Typography level="body-sm" color="primary">
              {selectedCount} selected
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            variant="soft"
            color="success"
            startDecorator={<DoneIcon />}
            onClick={onBulkDone}
            disabled={selectedCount === 0}
            size="sm"
          >
            DONE
          </Button>
          <Button
            variant="soft"
            color="warning"
            startDecorator={<SnoozeIcon />}
            onClick={onBulkSnooze}
            disabled={selectedCount === 0}
            size="sm"
          >
            SNOOZE
          </Button>
        </Stack>
      </Stack>

      {/* Middle Row: Content Filters */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ gap: 1.5, flexWrap: 'wrap' }}
      >
        <Tabs
          value={contentFilter}
          onChange={handleTabChange}
          sx={{ bgcolor: 'transparent', minWidth: 0, maxWidth: '100%', flexShrink: 0 }}
        >
          <TabList sx={{ flexWrap: 'nowrap', overflowX: 'auto', '& > [role="tab"]': { flexShrink: 0, whiteSpace: 'nowrap', px: { xs: 1, sm: 1.5 } } }}>
            <Tab value="all" indicatorInset>
              <Stack direction="row" spacing={1} alignItems="center">
                <AllInboxIcon size={18} />
                <Typography level="body-sm">ALL</Typography>
              </Stack>
            </Tab>
            <Tab value="email" indicatorInset>
              <Stack direction="row" spacing={1} alignItems="center">
                <EmailIcon size={18} />
                <Typography level="body-sm">EMAILS</Typography>
              </Stack>
            </Tab>
            <Tab value="sms" indicatorInset>
              <Stack direction="row" spacing={1} alignItems="center">
                <SmsIcon size={18} />
                <Typography level="body-sm">SMS</Typography>
              </Stack>
            </Tab>
            {/* Temporarily hidden: CALLS tab */}
            {false && (
              <Tab value="phone" indicatorInset>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PhoneIcon size={18} />
                  <Typography level="body-sm">CALLS</Typography>
                </Stack>
              </Tab>
            )}
            <Tab value="task" indicatorInset>
              <Stack direction="row" spacing={1} alignItems="center">
                <TaskIcon size={18} />
                <Typography level="body-sm">TASKS</Typography>
              </Stack>
            </Tab>
          </TabList>
        </Tabs>

        {/* Right Side: User and Sort Filters */}
        <Stack direction="row" spacing={1} sx={{ ml: 'auto', width: { xs: '100%', sm: 'auto' }, maxWidth: '100%', minWidth: 0 }}>
          <Select
            aria-label="Filter by user"
            value={assigneeFilter}
            onChange={(e, newValue) => onAssigneeFilterChange(newValue)}
            placeholder="All Users"
            size="sm"
            sx={{ flex: { xs: 1, sm: 'none' }, minWidth: { xs: 0, sm: 150 } }}
          >
            <Option value={null}>All Users</Option>
            {assignees.map((assignee) => (
              <Option key={assignee.id} value={assignee.id}>
                {assignee.name}
              </Option>
            ))}
          </Select>

          <Select
            aria-label="Sort inbox"
            value={sortOrder}
            onChange={(e, newValue) => onSortOrderChange(newValue as 'oldest' | 'newest' | 'priority')}
            size="sm"
            sx={{ flex: { xs: 1, sm: 'none' }, minWidth: { xs: 0, sm: 180 } }}
          >
            <Option value="oldest">Sort By: {sortLabels.oldest}</Option>
            <Option value="newest">Sort By: {sortLabels.newest}</Option>
            <Option value="priority">Sort By: {sortLabels.priority}</Option>
          </Select>
        </Stack>

      </Stack>
    </Box>
  );
};

export default UnifiedInboxHeader;
